import React, { createContext, useContext, useMemo } from 'react';

import { FacilitiesFilterState } from './facilitiesFilterTypes';
import { immer } from 'zustand/middleware/immer';
import { createWithEqualityFn } from 'zustand/traditional';
import {
  computeTotalsRow,
  useFacilitiesTableData,
} from '~/core/routes/credit-facilities/hooks/useFacilitiesTableData';
import { useFetchFacilitiesTableData } from '~/core/routes/credit-facilities/hooks/useFetchFacilitiesTableData';

export type PortfolioFacilitiesStore = {
  facilitiesTableData: NonNullable<ReturnType<typeof useFacilitiesTableData>>;
  setFacilitiesTableData: (
    data: NonNullable<ReturnType<typeof useFacilitiesTableData>>,
  ) => void;

  filters: FacilitiesFilterState;
  initialFilters: FacilitiesFilterState;

  setFilters: (filters: FacilitiesFilterState) => void;
  setInitialFilters: (filters: FacilitiesFilterState) => void;
};

const CreateUsePortfolioFacilitiesStore = () =>
  createWithEqualityFn<PortfolioFacilitiesStore>()(
    immer((set) => ({
      facilitiesTableData: {
        error: undefined,
        computedRows: [],
        isHasBorrowingBaseLoading: false,
        isBalanceSheetExposureLoading: false,
        isFacilitiesTableDataLoading: false,
        totalsRow: computeTotalsRow([], []),
        hasBorrowingBase: false,
        balanceSheetImpactCalcs: null,
        attributeValues: [],
        attributeFiltersOptions: [],
        isAnyLoading: false,
      },
      setFacilitiesTableData: (
        data: NonNullable<ReturnType<typeof useFacilitiesTableData>>,
      ) => set({ facilitiesTableData: data }),

      filters: {
        staticFilters: { facilityIds: { selectedOptions: [], options: [] } },
        attributeFilters: {},
      },
      initialFilters: {
        staticFilters: { facilityIds: { selectedOptions: [], options: [] } },
        attributeFilters: {},
      },

      setFilters: (newFilters: FacilitiesFilterState) =>
        set({ filters: newFilters }),
      setInitialFilters: (newFilters: FacilitiesFilterState) =>
        set({ initialFilters: newFilters }),
    })),
  );

export type UsePortfolioFacilitiesStore = ReturnType<
  typeof CreateUsePortfolioFacilitiesStore
>;

export type PortfolioFacilitiesContextValue = {
  useStore: UsePortfolioFacilitiesStore;
};

const PortfolioFacilitiesContext = createContext<
  PortfolioFacilitiesContextValue | undefined
>(undefined);

export const usePortfolioFacilitiesContext = () => {
  const context = useContext(PortfolioFacilitiesContext);
  if (context === undefined) {
    throw new Error(
      'usePortfolioFacilitiesContext must be used within a PortfolioFacilitiesProvider',
    );
  }
  return context;
};

export const usePortfolioFacilitiesStore = <T,>(
  selector: (state: PortfolioFacilitiesStore) => T,
  equalityFn?: (a: T, b: T) => boolean,
): T => {
  const { useStore } = usePortfolioFacilitiesContext();
  return useStore(selector, equalityFn);
};

export const PortfolioFacilitiesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const useStore = useMemo(CreateUsePortfolioFacilitiesStore, []);

  useFetchFacilitiesTableData(useStore);

  const contextValue: PortfolioFacilitiesContextValue = useMemo(
    () => ({ useStore }),
    [useStore],
  );

  return (
    <PortfolioFacilitiesContext.Provider value={contextValue}>
      {children}
    </PortfolioFacilitiesContext.Provider>
  );
};
